/**
 * Ask for an advance on an estimate. With an online gateway the customer pays a link and it is
 * recorded automatically; with a UPI ID only, they scan a QR and you mark it received.
 * With neither, the panel says how to set one up instead of hiding.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, HandCoins, Loader2, CheckCircle2, CreditCard, MessageCircle, QrCode } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, renderMessage, sharePdfToWhatsApp } from '../../../lib/waShare';
import { advanceQrPng } from './advanceQr';
import { formatErr, inputCls } from './shared';

const inr = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const PRESETS = [25, 50, 70];
const upiUri = (vpa, name, amt, note) => `upi://pay?pa=${encodeURIComponent(vpa)}&pn=${encodeURIComponent(name || vpa)}&am=${Number(amt).toFixed(2)}&cu=INR&tn=${encodeURIComponent(note.slice(0, 40))}`;
const SETTINGS = '/admin/settings?tab=invoicing#payments';

const ConnectCard = () => (
  <section className="bg-white border border-dashed border-slate-300 rounded-xl p-4 space-y-3" data-testid="deposit-connect-card">
    <div className="flex items-center gap-2"><HandCoins size={15} className="text-teal-600" /><h3 className="text-sm font-semibold text-slate-900">Ask for an advance</h3></div>
    <p className="text-xs text-slate-500">Set up how customers pay you, and every estimate can ask for an advance before work starts.</p>
    <div className="grid gap-2 sm:grid-cols-2">
      <Link to={SETTINGS} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 hover:border-teal-500" data-testid="deposit-connect-gateway-btn">
        <CreditCard size={16} className="mt-0.5 text-teal-600" /><span className="text-xs"><b className="block text-sm text-slate-900">Connect payment gateway</b>Razorpay or Cashfree. Card, UPI and netbanking link, recorded automatically.</span>
      </Link>
      <Link to={SETTINGS} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 hover:border-teal-500" data-testid="deposit-add-upi-btn">
        <QrCode size={16} className="mt-0.5 text-teal-600" /><span className="text-xs"><b className="block text-sm text-slate-900">Use UPI QR</b>Add your UPI ID. Customer scans and pays; you mark it received.</span>
      </Link>
    </div>
  </section>
);

const MarkReceived = ({ estimateId, due, onDone }) => {
  const { authHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ amount: due, method: 'upi', reference: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/payments/estimate/${estimateId}/deposit/received`, { ...v, amount: Number(v.amount) }, { headers: authHeader() });
      setOpen(false); onDone(data);
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => { setV((x) => ({ ...x, amount: due })); setOpen(true); }} className="text-xs font-semibold text-teal-700 hover:text-teal-900" data-testid="deposit-mark-received-btn">Mark advance received</button>;
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 p-2" data-testid="deposit-mark-form">
      <span className="text-slate-400 text-sm">₹</span>
      <input type="number" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} className={`${inputCls} w-24 py-1 text-sm`} data-testid="deposit-received-amount" />
      <select value={v.method} onChange={(e) => setV({ ...v, method: e.target.value })} className={`${inputCls} w-24 py-1 text-sm`} data-testid="deposit-received-method">
        {['upi', 'cash', 'bank', 'card', 'cheque'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
      </select>
      <input value={v.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} placeholder="UTR / ref" className={`${inputCls} w-28 py-1 text-sm`} data-testid="deposit-received-ref" />
      <button type="button" disabled={busy || !Number(v.amount)} onClick={save} className="text-xs font-semibold bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-3 py-1.5 disabled:bg-teal-300" data-testid="deposit-received-save">{busy ? <Loader2 className="animate-spin" size={13} /> : 'Save'}</button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500" data-testid="deposit-received-cancel">Cancel</button>
      {err && <p className="w-full text-xs text-red-700" data-testid="deposit-received-err">{err}</p>}
    </div>
  );
};

export const DepositPanel = ({ estimateId, total, number = '', customer, vehicle, onPaid }) => {
  const { authHeader } = useAuth();
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/estimate/${estimateId}/pay-link`, { headers: authHeader() });
      setInfo(data);
    } catch { setInfo(null); }
  }, [estimateId, authHeader]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); if (onPaid) onPaid(); };

  // Thank-you message with the advance receipt PDF.
  const sendReceipt = async (dep, rows) => {
    const idx = rows.findIndex((r) => r.id === dep.id);
    const upto = rows.slice(0, idx + 1).reduce((a, r) => a + Number(r.amount || 0), 0);
    let link = '';
    try {
      const { data } = await axios.post(`${API}/billing/estimates/${estimateId}/deposits/${dep.id}/receipt-link`, {}, { headers: authHeader() });
      link = data.receipt_url.startsWith('http') ? data.receipt_url : `${window.location.origin}${data.receipt_url}`;
    } catch { /* message still goes without a link */ }
    const receiptNo = `AR-${number || estimateId.slice(0, 8)}-${idx + 1}`;
    const vals = { customer: greetName(customer?.prefix, customer?.name), amount: inr(dep.amount), number, receipt_no: receiptNo,
      balance: inr(Math.max(0, Number(total || 0) - upto)), pdf_link: link };
    const message = renderMessage('advance_receipt', vals)
      || `Thank you! We have received your advance of ₹ ${vals.amount} for estimate ${number}.\nReceipt no: ${receiptNo}${link ? `\n${link}` : ''}`;
    await sharePdfToWhatsApp({
      getPdf: async () => (await axios.get(`${API}/billing/estimates/${estimateId}/deposits/${dep.id}/receipt`, { headers: authHeader(), responseType: 'blob' })).data,
      filename: `Advance_Receipt_${receiptNo.replace(/\//g, '_')}.pdf`, title: `Receipt ${receiptNo}`, message, phone: customer?.phone || '', kind: 'Receipt',
    });
  };
  const afterReceived = (data) => {
    refresh();
    const rows = data?.deposits || [];
    if (rows.length) sendReceipt(rows[rows.length - 1], rows).catch(() => {});
  };
  const openReceipt = async (dep) => {
    try {
      const { data } = await axios.get(`${API}/billing/estimates/${estimateId}/deposits/${dep.id}/receipt`, { headers: authHeader(), responseType: 'blob' });
      window.open(URL.createObjectURL(data), '_blank');
    } catch (e) { setErr(formatErr(e)); }
  };

  // Opens WhatsApp (share sheet on phones) with the advance message and a QR image.
  const sendWa = async (d) => {
    const v = d.upi_vpa || d.fallback_upi;
    const amt = Math.max(0, Number(d.deposit_requested || 0) - Number(d.deposit_paid || 0));
    const value = d.pay_url || d.upi_uri || (v ? upiUri(v, d.payee_name, amt, `Advance for estimate ${number}`.trim()) : '');
    if (!amt || !value) return;
    const message = renderMessage('advance_request', {
      customer: greetName(customer?.prefix, customer?.name), number, total: inr(total), deposit: inr(amt),
      vehicle: vehicle ? [vehicle.make, vehicle.model].filter(Boolean).join(' ') + (vehicle.registration_number ? ` (${vehicle.registration_number})` : '') : '',
      pay_link: d.pay_url || '', upi: v || '',
    }) || `Please pay an advance of ₹ ${inr(amt)} for estimate ${number}.${d.pay_url ? `\nPay online: ${d.pay_url}` : ''}${v ? `\nUPI ID: ${v}` : ''}`;
    await sharePdfToWhatsApp({
      getPdf: () => advanceQrPng({ value, amount: amt, payee: d.payee_name, vpa: d.pay_url ? '' : v, number }),
      filename: `Advance_QR_${String(number || estimateId.slice(0, 8)).replace(/\//g, '_')}.png`,
      title: `Advance for ${number}`, message, phone: customer?.phone || '', type: 'image/png', kind: 'QR',
    });
  };

  const put = async (body) => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.put(`${API}/payments/estimate/${estimateId}/deposit`, body, { headers: authHeader() });
      setAmount('');
      const { data: fresh } = await axios.get(`${API}/payments/estimate/${estimateId}/pay-link`, { headers: authHeader() });
      setInfo(fresh);
      if (onPaid) onPaid(data);
      if (!body.clear) sendWa(fresh).catch(() => {});
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  const save = (value) => put(PRESETS.includes(value) ? { percent: value } : { amount: Number(value) });
  const copy = async () => {
    try { await navigator.clipboard.writeText(info.pay_url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard blocked */ }
  };

  if (!info) return null;
  const vpa = info.upi_vpa || info.fallback_upi;
  if (!info.connected && !vpa && !Number(info.deposit_paid)) return <ConnectCard />;

  const requested = Number(info.deposit_requested || 0);
  const paid = Number(info.deposit_paid || 0);
  const due = Math.max(0, Math.round((requested - paid) * 100) / 100);
  const qr = info.pay_url || info.upi_uri || (vpa && due > 0 ? upiUri(vpa, info.payee_name, due, `Advance for estimate ${number}`.trim()) : '');
  const manual = !info.connected || !info.auto_reconcile;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="deposit-panel">
      <div className="flex items-center gap-2">
        <HandCoins size={15} className="text-teal-600" />
        <h3 className="text-sm font-semibold text-slate-900">Ask for an advance</h3>
        {!info.connected && <Link to={SETTINGS} className="ml-auto text-[11px] font-semibold text-slate-400 hover:text-teal-700" data-testid="deposit-connect-link">Connect gateway for auto-recording</Link>}
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5" data-testid="deposit-err">{err}</div>}

      {!requested && !paid ? (
        <>
          <p className="text-xs text-slate-500">
            {manual ? 'Take an advance before work starts. The customer scans your UPI QR; mark it received and it comes off the final invoice.'
              : 'Take an advance before work starts. The customer gets a link; the amount is adjusted against the final invoice automatically.'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" disabled={busy} onClick={() => save(p)} className="text-xs font-semibold border border-slate-300 hover:border-teal-500 hover:text-teal-700 rounded-full px-3 py-1.5 disabled:opacity-50" data-testid={`deposit-preset-${p}`}>
                {p}% · ₹ {inr(Math.round((total || 0) * p / 100))}
              </button>))}
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-sm">₹</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Other" className={`${inputCls} w-28 py-1.5 text-sm`} data-testid="deposit-amount-input" />
              <button type="button" disabled={busy || !amount} onClick={() => save(amount)} className="text-xs font-semibold bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-3 py-1.5 disabled:bg-teal-300" data-testid="deposit-set-btn">
                {busy ? <Loader2 className="animate-spin" size={13} /> : 'Set'}
              </button>
            </div>
          </div>
          {manual && <MarkReceived estimateId={estimateId} due={total} onDone={afterReceived} />}
        </>
      ) : (
        <div className="flex items-start gap-4">
          {due > 0 && qr && <div className="shrink-0 p-2 bg-white border border-slate-200 rounded-lg" data-testid="deposit-qr"><QRCodeSVG value={qr} size={96} level="M" /></div>}
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="text-sm text-slate-900">Advance requested: <b data-testid="deposit-requested">₹ {inr(requested)}</b>{paid > 0 && <> · received <b className="text-emerald-700" data-testid="deposit-paid">₹ {inr(paid)}</b></>}</div>
            {due > 0 && info.pay_url && (
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-600 truncate" data-testid="deposit-pay-url">{info.pay_url}</code>
                <button onClick={copy} className="shrink-0 text-slate-400 hover:text-teal-600" data-testid="copy-deposit-link-btn">{copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}</button>
              </div>)}
            {due > 0 && !info.pay_url && vpa && <p className="text-xs text-slate-500" data-testid="deposit-upi-hint">Scan to pay ₹ {inr(due)} to <span className="font-mono">{vpa}</span></p>}
            {due <= 0 && <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="deposit-settled"><CheckCircle2 size={14} /> Advance received in full</div>}
            <div className="flex flex-wrap items-center gap-3 pt-0.5">
              {due > 0 && manual && <MarkReceived estimateId={estimateId} due={due} onDone={afterReceived} />}
              {due > 0 && qr && <button type="button" onClick={() => sendWa(info)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700" data-testid="deposit-send-wa-btn"><MessageCircle size={13} /> Send on WhatsApp</button>}
            </div>
            {due > 0 && !info.pay_url && vpa && !vpa.includes('@') && <p className="text-[11px] text-amber-700" data-testid="deposit-upi-warning">Your UPI ID “{vpa}” looks incomplete. UPI apps need the full ID, like name@okhdfc. <Link to={SETTINGS} className="font-semibold underline">Fix it in Settings</Link></p>}
            {paid <= 0 && <button type="button" onClick={() => put({ clear: true })} disabled={busy} className="block text-[11px] font-semibold text-slate-400 hover:text-rose-600" data-testid="clear-deposit-btn">Remove advance request</button>}
          </div>
        </div>
      )}
      {!!info.deposits?.length && (
        <div className="border-t border-slate-100 pt-2" data-testid="deposit-receipts">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Advances received</p>
          {info.deposits.map((d, i) => (
            <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1 text-xs" data-testid={`deposit-row-${d.id}`}>
              <span className="text-slate-500">{(d.date || '').slice(0, 10)}</span>
              <b className="text-slate-900">₹ {inr(d.amount)}</b>
              <span className="text-slate-500">{String(d.method || '').toUpperCase()}{d.reference ? ` · ${d.reference}` : ''}</span>
              <span className="font-mono text-[11px] text-slate-400">AR-{number}-{i + 1}</span>
              <span className="ml-auto flex items-center gap-3">
                <button type="button" onClick={() => openReceipt(d)} className="font-semibold text-slate-500 hover:text-slate-900" data-testid={`deposit-receipt-view-${d.id}`}>View receipt</button>
                <button type="button" onClick={() => sendReceipt(d, info.deposits)} className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:text-emerald-900" data-testid={`deposit-receipt-send-${d.id}`}><MessageCircle size={12} /> Send receipt</button>
              </span>
            </div>))}
        </div>)}
    </section>
  );
};

export default DepositPanel;
